import useClientMetadata from "hooks/useClientMetadata"
import { useEffect, useMemo, useState } from "react"
import { groupBy, stripHTML } from "lib/utils"
import { useNotifications } from "lib/NotificationsProvider"
import { useOrbit } from "providers/OrbitProvider"
import type {
  ClientCampaignRecord,
  ClientRecord,
  DegreeProgramRecord,
  VendorRecord,
} from 'lib/DataModel'
import type { LookupMap, StringOptionMap } from './table/leadTableTypes'
import type { LeadTableLookupFilterGroup } from './filters/LeadTableLookupFilter'

type MetadataState = {
  loading: boolean
  clientIdToName: LookupMap
  clientCampaignIdToName: LookupMap
  vendorIdToName: LookupMap
  degreeProgramIdToName: LookupMap
  degreeProgramsByClient: LeadTableLookupFilterGroup[]
}

const INITIAL_METADATA_STATE: MetadataState = {
  loading: true,
  clientIdToName: {},
  clientCampaignIdToName: {},
  vendorIdToName: {},
  degreeProgramIdToName: {},
  degreeProgramsByClient: [],
}

function mapToName(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  records: Array<{ id: string; attributes: Record<string, any> }>,
  getRemoteId: (record: { id: string }) => string | null | undefined
): LookupMap {
  return records.reduce<LookupMap>((hash, record) => {
    const remoteId = getRemoteId(record)

    if (!remoteId) {
      return hash
    }

    hash[String(remoteId)] = stripHTML(
      record.attributes.internalName ||
        record.attributes.name ||
        record.attributes.description ||
        ''
    )

    return hash
  }, {})
}

export function useLeadTableMeta({
  accessibleClientIds,
}: {
  accessibleClientIds: string[]
}) {
  const { remote, getRemoteId } = useOrbit()
  const { addNotification } = useNotifications()

  const [metadataState, setMetadataState] = useState<MetadataState>(INITIAL_METADATA_STATE)

  useEffect(() => {
    let cancelled = false

    async function loadMetadata() {
      setMetadataState((prev) => ({
        ...prev,
        loading: true,
      }))

      try {
        const [clients, vendors, degreePrograms, clientCampaigns] = await Promise.all([
          remote.query<ClientRecord[]>((q) => q.findRecords('client')),
          remote.query<VendorRecord[]>((q) => q.findRecords('vendor')),
          remote.query<DegreeProgramRecord[]>(
            (q) => q.findRecords('degreeProgram'),
            {
              sources: {
                remote: {
                  settings: {
                    params: {
                      fields: {
                        'degree-program': ['name', 'client'].join(','),
                      },
                    },
                  },
                },
              },
            }
          ),
          remote.query<ClientCampaignRecord[]>((q) => q.findRecords('clientCampaign')),
        ])

        if (cancelled) {
          return
        }

        const clientIdToName = mapToName(clients, getRemoteId)
        const clientCampaignIdToName = mapToName(clientCampaigns, getRemoteId)

        const degreeProgramsByClient = groupBy(
          degreePrograms,
          'relationships.client.data.id',
          {
            transform: (degreeProgram: DegreeProgramRecord) => degreeProgram.id,
          }
        ) as Record<string, string[]>

        const degreeProgramGroups = Object.entries(degreeProgramsByClient)
          .map(([clientId, degreeProgramIds]) => ({
            title: clientIdToName[clientId] ?? `Client ${clientId}`,
            keys: degreeProgramIds,
          }))
          .sort((a, b) => a.title.localeCompare(b.title))

        setMetadataState({
          loading: false,
          clientIdToName,
          clientCampaignIdToName,
          vendorIdToName: mapToName(vendors, getRemoteId),
          degreeProgramIdToName: mapToName(degreePrograms, getRemoteId),
          degreeProgramsByClient: degreeProgramGroups,
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        if (cancelled) {
          return
        }

        addNotification({ variant: 'alert', message: 'Error loading leads metadata' })

        setMetadataState((prev) => ({
          ...prev,
          loading: false,
        }))
      }
    }

    loadMetadata()

    return () => {
      cancelled = true
    }
  }, [addNotification, getRemoteId, remote])

  const {
    statuses,
    dispositions,
    clientCampaignCustomAttributes,
  } = useClientMetadata(accessibleClientIds)

  const normalizedStatuses = useMemo<StringOptionMap>(() => {
    return statuses ?? {}
  }, [statuses])

  const normalizedDispositions = useMemo<StringOptionMap>(() => {
    return dispositions ?? {}
  }, [dispositions])

  const normalizedClientCampaignCustomAttributes = useMemo<string[]>(() => {
    return clientCampaignCustomAttributes ?? []
  }, [clientCampaignCustomAttributes])

  return {
    loadingMetadata: metadataState.loading,
    clientIdToName: metadataState.clientIdToName,
    clientCampaignIdToName: metadataState.clientCampaignIdToName,
    vendorIdToName: metadataState.vendorIdToName,
    degreeProgramIdToName: metadataState.degreeProgramIdToName,
    degreeProgramsByClient: metadataState.degreeProgramsByClient,
    statuses: normalizedStatuses,
    dispositions: normalizedDispositions,
    clientCampaignCustomAttributes: normalizedClientCampaignCustomAttributes,
  }
}
